import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

interface SpeakerContextMenuProps {
  isOpen: boolean;
  buttonRef: React.RefObject<HTMLElement> | null; // Reference to the button that triggered the menu
  onClose: () => void;
  onEdit: () => void;
  onAddToStory?: () => void; // Callback for adding speaker to story
  onRemoveFromStory?: () => void; // Callback for removing speaker from story
  onUnassign?: () => void; // Callback for unassigning speaker (removing from voice mappings)
  speakerName: string;
  isAnonymous?: boolean; // Flag to indicate if this is an anonymous speaker
  showAddToStory?: boolean; // Flag to show "Add to Story" option
  showRemoveFromStory?: boolean; // Flag to show "Remove from Story" option
  showUnassign?: boolean; // Flag to show "Unassign Speaker" option
}

/**
 * Context menu for speaker actions that appears when clicking the overflow button
 */
const SpeakerContextMenu: React.FC<SpeakerContextMenuProps> = ({
  isOpen,
  buttonRef,
  onClose,
  onEdit,
  onAddToStory,
  onRemoveFromStory,
  onUnassign,
  speakerName,
  isAnonymous = false,
  showAddToStory = false,
  showRemoveFromStory = false,
  showUnassign = false
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Handle escape key press
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Calculate position relative to button
  useEffect(() => {
    if (!isOpen || !menuRef.current || !buttonRef?.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuElement = menuRef.current;

    // Position menu to the left of the button
    menuElement.style.position = 'fixed';
    menuElement.style.top = `${buttonRect.top}px`;
    menuElement.style.left = `${buttonRect.left - menuElement.offsetWidth - 5}px`;
  }, [isOpen, buttonRef]);

  if (!isOpen) return null;

  return (
    <MenuContainer
      ref={menuRef}
      data-speaker-menu={true}
    >
      <MenuHeader>
        {speakerName}
      </MenuHeader>
      <MenuItem onClick={onEdit}>
        <MenuIcon>✏️</MenuIcon>
        {isAnonymous ? 'Create & Assign Speaker' : 'Edit Speaker'}
      </MenuItem>
      {showAddToStory && onAddToStory && !isAnonymous && (
        <MenuItem onClick={() => {
          onAddToStory();
          onClose();
        }}>
          <MenuIcon>📝</MenuIcon>
          Add to Story
        </MenuItem>
      )}
      {showRemoveFromStory && onRemoveFromStory && !isAnonymous && (
        <MenuItem onClick={() => {
          onRemoveFromStory();
          onClose();
        }}>
          <MenuIcon>🗑️</MenuIcon>
          Remove from Story
        </MenuItem>
      )}
      {showUnassign && onUnassign && !isAnonymous && (
        <MenuItem onClick={() => {
          onUnassign();
          onClose();
        }}>
          <MenuIcon>🔗</MenuIcon>
          Unassign Speaker
        </MenuItem>
      )}
    </MenuContainer>
  );
};

const MenuContainer = styled.div`
  position: fixed; /* Will be positioned programmatically in useEffect */
  min-width: 180px;
  background-color: #1a1a2a;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  z-index: 1000;
`;

const MenuHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #f2d093;
  padding: 12px 16px;
  border-bottom: 1px solid #2d2d42;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 16px;
  font-size: 14px;
  color: #f3e9d7;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #2a2f36;
  }

  &:active {
    background-color: #3d4249;
  }
`;

const MenuIcon = styled.span`
  margin-right: 10px;
  font-size: 16px;
`;

export default SpeakerContextMenu;